// Ported verbatim from files/snapdraw-design-kit/code/src/lib/i18n/strings/lineitems.ts
// (Lovable reference implementation), backlog G4 (2026-08-24) — the
// catalog item sheet, line-item picker, client picker, and the Quote
// builder's line/business/client/sales-tax copy. Kept as its own file,
// matching the kit's own separation of this system from documents.ts.
export const en = {
  // Catalog item sheet
  "catalog.sheet.newTitle": "New catalog item",
  "catalog.sheet.editTitle": "Edit catalog item",
  "catalog.sheet.body": "Save a rate once and reuse it on every quote. Quotes keep the rate they were built with.",
  "catalog.sheet.name": "Item name",
  "catalog.sheet.namePlaceholder": "Journeyman Electrician",
  "catalog.sheet.nameError": "Give the item a name your crew will recognize.",
  "catalog.sheet.type": "Type",
  "catalog.sheet.unit": "Unit",
  "catalog.sheet.unitOther": "Other",
  // Split by category (2026-09-05): "Default rate" was ambiguous enough
  // that a user could easily enter their intended bill price for Material/
  // Equipment instead of their cost, then get silently double-marked-up on
  // every quote (Markup % always applies on top). Labor keeps "Rate" since
  // a labor rate already is the bill rate — it's never marked up.
  "catalog.sheet.rate.material": "Unit cost",
  "catalog.sheet.rate.labor": "Rate",
  "catalog.sheet.rateHint.material": "What you pay. Markup gets added automatically at quote time.",
  "catalog.sheet.rateHint.labor": "What you bill for this item. Labor is never marked up.",
  "catalog.sheet.rateError": "Enter a rate greater than zero.",
  "catalog.sheet.code": "Code",
  "catalog.sheet.codeError": "That code is already in your catalog.",
  "catalog.sheet.codeHint": "Auto-suggested from the type and name — edit it if you use your own numbering.",
  "catalog.sheet.note": "Note (optional)",
  "catalog.sheet.notePlaceholder": "Spec, size or vendor",
  "catalog.sheet.taxable": "Taxable by default",
  "catalog.sheet.taxableHint": "Applied when the item lands on a quote. The line's own taxable checkbox still wins.",
  "catalog.sheet.snapshotHint": "Used on {count} documents. Changing the rate only affects new quotes.",
  "catalog.sheet.delete": "Delete",
  "catalog.sheet.archive": "Archive",
  "catalog.sheet.cancel": "Cancel",
  "catalog.sheet.save": "Save item",
  "catalog.saved.toast": "{name} saved to your catalog",
  "catalog.deleted.toast": "{name} deleted",
  "catalog.archived.toast": "{name} archived — old documents keep it",
  "catalog.restored.toast": "{name} restored to your catalog",
  "catalog.archivedSection": "Archived",
  "catalog.restore": "Restore",
  "catalog.editAria": "Edit {name}",

  // Line item picker
  "picker.title": "Add {type}",
  "picker.body": "Pick a saved rate or price this one by hand.",
  "picker.search": "Search codes, items…",
  "picker.empty": "Nothing saved yet for this type — enter it manually and save it for next time.",
  "picker.manual": "Enter manually",

  // Quote line editing
  "docs.quote.addEquipment": "Equipment line",
  "docs.quote.line.description": "Description",
  "docs.quote.line.qty": "Qty",
  "docs.quote.line.unit": "Unit",
  "docs.quote.line.rate": "Rate",
  "docs.quote.line.total": "Total",
  "docs.quote.line.markup": "Markup",
  "docs.quote.line.markup.reset": "Reset markup",
  "docs.quote.line.markup.overriddenTooltip": "Overridden — click ↺ to follow the quote's {pct}%",
  "docs.quote.line.remove": "Remove line",
  "docs.quote.line.taxable": "Taxable",
  "docs.quote.line.exempt": "Exempt from sales tax",
  "docs.quote.line.taxShort": "Tax",
  "docs.quote.line.exemptShort": "Exempt",
  "docs.quote.line.adjusted": "Adjusted",
  "docs.quote.line.saveToCatalog": "Save to catalog",
  "docs.quote.line.namePlaceholder": "Describe the work or material",
  "docs.quote.total.salesTaxPct": "Sales tax ({pct}%)",
  "docs.quote.total.equipment": "Materials & equipment",

  // Business prefill
  "docs.quote.business.source": "From your account settings",
  "docs.quote.business.edit": "Edit",
  "docs.quote.business.summaryEdit": "Change on this quote",
  "docs.quote.business.collapse": "Collapse",

  // Client linkage
  "docs.quote.client.choose": "Choose client",
  "docs.quote.client.view": "Open client record",
  "docs.quote.client.change": "Change",
  "docs.quote.client.first": "Start here. Terms, job address and tax all follow the client.",
  "docs.quote.client.none": "No client selected. Quotes attach to a client record so terms and aging stay accurate.",
  "docs.quote.client.retainage": "{pct}% retainage on pay applications",
  "docs.quote.client.termsApplied": "Terms set to {terms} from {name}",
  "clientPicker.title": "Select client",
  "clientPicker.body": "Contact, job address and payment terms come from the client record.",
  "clientPicker.search": "Search clients…",
  "clientPicker.empty": "No client matches that search.",
  "clientPicker.new": "New client",

  // Sales tax
  "docs.quote.tax.state": "{state} state base {rate}% — confirm your local rate",
  "docs.quote.tax.unknown": "Pick a client with a job address to prefill the state rate",
  "docs.quote.tax.treatment.none": "{state}: no state sales tax",
  "docs.quote.tax.treatment.laborInclusive": "{state}: materials, equipment and labor all taxable",
  "docs.quote.tax.treatment.goodsOnly": "{state}: materials and equipment taxable, labor exempt",
  "docs.quote.tax.overridden": "Your rate, saved for this client",
  "docs.quote.tax.reset": "Use state base",
  "docs.quote.tax.base": "Taxable base",
  "docs.quote.tax.lineCount": "{taxed} taxed · {exempt} exempt",
  "docs.quote.tax.afterMarkup": "Tax applies after markup",
} as const;

export const es: Record<keyof typeof en, string> = {
  "catalog.sheet.newTitle": "Nuevo ítem de catálogo",
  "catalog.sheet.editTitle": "Editar ítem de catálogo",
  "catalog.sheet.body": "Guarda una tarifa una vez y reúsala en cada cotización. Las cotizaciones conservan su tarifa.",
  "catalog.sheet.name": "Nombre del ítem",
  "catalog.sheet.namePlaceholder": "Electricista oficial",
  "catalog.sheet.nameError": "Dale un nombre que tu cuadrilla reconozca.",
  "catalog.sheet.type": "Tipo",
  "catalog.sheet.unit": "Unidad",
  "catalog.sheet.unitOther": "Otra",
  "catalog.sheet.rate.material": "Costo unitario",
  "catalog.sheet.rate.labor": "Tarifa",
  "catalog.sheet.rateHint.material": "Lo que pagas. El margen se agrega automáticamente al cotizar.",
  "catalog.sheet.rateHint.labor": "Lo que cobras por este ítem. La mano de obra nunca lleva margen.",
  "catalog.sheet.rateError": "Ingresa una tarifa mayor que cero.",
  "catalog.sheet.code": "Código",
  "catalog.sheet.codeError": "Ese código ya existe en tu catálogo.",
  "catalog.sheet.codeHint": "Sugerido según el tipo y el nombre — edítalo si usas tu propia numeración.",
  "catalog.sheet.note": "Nota (opcional)",
  "catalog.sheet.notePlaceholder": "Especificación, medida o proveedor",
  "catalog.sheet.taxable": "Gravable por defecto",
  "catalog.sheet.taxableHint": "Se aplica al agregarlo a una cotización. La casilla gravable de la línea manda.",
  "catalog.sheet.snapshotHint": "Usado en {count} documentos. Cambiar la tarifa solo afecta cotizaciones nuevas.",
  "catalog.sheet.delete": "Eliminar",
  "catalog.sheet.archive": "Archivar",
  "catalog.sheet.cancel": "Cancelar",
  "catalog.sheet.save": "Guardar ítem",
  "catalog.saved.toast": "{name} se guardó en tu catálogo",
  "catalog.deleted.toast": "{name} eliminado",
  "catalog.archived.toast": "{name} archivado — los documentos antiguos lo conservan",
  "catalog.restored.toast": "{name} restaurado en tu catálogo",
  "catalog.archivedSection": "Archivados",
  "catalog.restore": "Restaurar",
  "catalog.editAria": "Editar {name}",

  "picker.title": "Agregar {type}",
  "picker.body": "Elige una tarifa guardada o cotiza esta a mano.",
  "picker.search": "Buscar códigos, ítems…",
  "picker.empty": "Aún no hay nada de este tipo — ingrésalo manualmente y guárdalo para la próxima.",
  "picker.manual": "Ingresar manualmente",

  "docs.quote.addEquipment": "Línea de equipo",
  "docs.quote.line.description": "Descripción",
  "docs.quote.line.qty": "Cant.",
  "docs.quote.line.unit": "Unidad",
  "docs.quote.line.rate": "Tarifa",
  "docs.quote.line.total": "Total",
  "docs.quote.line.markup": "Margen",
  "docs.quote.line.markup.reset": "Restablecer margen",
  "docs.quote.line.markup.overriddenTooltip": "Anulado — haz clic en ↺ para seguir el {pct}% de la cotización",
  "docs.quote.line.remove": "Quitar línea",
  "docs.quote.line.taxable": "Gravable",
  "docs.quote.line.exempt": "Exenta de impuesto",
  "docs.quote.line.taxShort": "Grav.",
  "docs.quote.line.exemptShort": "Exenta",
  "docs.quote.line.adjusted": "Ajustada",
  "docs.quote.line.saveToCatalog": "Guardar en catálogo",
  "docs.quote.line.namePlaceholder": "Describe el trabajo o material",
  "docs.quote.total.salesTaxPct": "Impuesto sobre ventas ({pct}%)",
  "docs.quote.total.equipment": "Materiales y equipo",

  "docs.quote.business.source": "Desde la configuración de tu cuenta",
  "docs.quote.business.edit": "Editar",
  "docs.quote.business.summaryEdit": "Cambiar para esta cotización",
  "docs.quote.business.collapse": "Contraer",

  "docs.quote.client.choose": "Elegir cliente",
  "docs.quote.client.view": "Abrir registro del cliente",
  "docs.quote.client.change": "Cambiar",
  "docs.quote.client.first": "Empieza aquí. Los términos, la dirección de obra y el impuesto siguen al cliente.",
  "docs.quote.client.none": "Sin cliente. Las cotizaciones se ligan a un cliente para mantener términos y antigüedad correctos.",
  "docs.quote.client.retainage": "{pct}% de retención en solicitudes de pago",
  "docs.quote.client.termsApplied": "Términos: {terms} según {name}",
  "clientPicker.title": "Seleccionar cliente",
  "clientPicker.body": "Contacto, dirección de obra y términos vienen del registro del cliente.",
  "clientPicker.search": "Buscar clientes…",
  "clientPicker.empty": "Ningún cliente coincide con esa búsqueda.",
  "clientPicker.new": "Nuevo cliente",

  "docs.quote.tax.state": "Base estatal de {state}: {rate}% — confirma tu tasa local",
  "docs.quote.tax.unknown": "Elige un cliente con dirección de obra para precargar la tasa estatal",
  "docs.quote.tax.treatment.none": "{state}: sin impuesto estatal sobre ventas",
  "docs.quote.tax.treatment.laborInclusive": "{state}: materiales, equipo y mano de obra, todo gravable",
  "docs.quote.tax.treatment.goodsOnly": "{state}: materiales y equipo gravables, mano de obra exenta",
  "docs.quote.tax.overridden": "Tu tasa, guardada para este cliente",
  "docs.quote.tax.reset": "Usar base estatal",
  "docs.quote.tax.base": "Base gravable",
  "docs.quote.tax.lineCount": "{taxed} gravadas · {exempt} exentas",
  "docs.quote.tax.afterMarkup": "El impuesto se aplica después del margen",
};
